<template>
  <v-form v-model="valid" ref="form" class="dialog-content">
    <update-subscription-card
      class="mt-4"
      v-model:subscription="localValue.item"
      :subscription-rules="subscriptionRules"
      :is-form-valid="valid"
    />
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, type Ref, ref, watch } from "vue";
import {
  type Subscription,
  type SubscriptionRulesConfig,
  useMspStore,
} from "@/_store/msp/msp.module.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import cloneDeep from "lodash/cloneDeep";
import { SubscriptionAddon, SubscriptionBundle, SubscriptionModule } from "@/constants/workplaces";
import type { VuetifyFormRef } from "@/types";
import difference from "lodash/differenceBy";
import { getEnabledModules } from "@coronet/cui";

import { type ModulesSettings, useWorkspaceStore } from "@/_store/workspace.module.ts";
import UpdateSubscriptionCard from "@/components/msp/UpdateSubscriptionCard.vue";
import { setDefaultModules } from "@/_helpers/subscription";

export default defineComponent({
  components: { UpdateSubscriptionCard },
  props: {
    config: {
      type: Object as PropType<
        DialogDataConfig<
          Subscription & {
            modulesWhichRemovedDuringEdit?: SubscriptionModule[];
            workspaceId?: string;
          }
        >
      >,
      required: true,
    },
  },
  emits: ["update:valid", "update:localValue"],
  setup(props, { emit }) {
    const { workspace } = useWorkspaceStore();
    const subscriptionRules: Ref<SubscriptionRulesConfig> = ref({
      allowedModules: [],
      allowedBundles: [],
    });
    const selectedTab = ref(0);
    const valid = ref(true);
    const form = ref<VuetifyFormRef>();
    const localValue = ref<
      DialogDataConfig<Subscription & { modulesWhichRemovedDuringEdit?: SubscriptionModule[] }>
    >(
      cloneDeep({
        ...props.config,
        item: {
          bundles: props.config.item.bundles ?? [],
          modules: initModules(),
        },
      })
    );

    const modulesWhichRemovedDuringEdit = computed(() => {
      const oldModules = getEnabledModules(props.config.item);
      const newModules = getEnabledModules(localValue.value.item);
      const modulesWithoutTickets = [
        SubscriptionModule.NETWORK,
        SubscriptionModule.EDR,
        SubscriptionModule.MDM,
      ];
      return difference(oldModules, newModules).filter((module) => {
        return !modulesWithoutTickets.includes(module);
      });
    });

    const validate = async () => {
      const validationResult = await form.value?.validate();
      return !!validationResult?.valid;
    };

    watch(
      localValue,
      (value) => {
        const newValue = cloneDeep(value);
        newValue.item.modulesWhichRemovedDuringEdit = modulesWhichRemovedDuringEdit.value;
        emit("update:localValue", newValue);
      },
      // Emit initial state so the dialog action receives a fully shaped payload
      // even when the user doesn't change any fields.
      { deep: true, immediate: true }
    );

    watch(
      valid,
      (newVal: boolean | null) => {
        emit("update:valid", newVal ?? true);
      },
      { immediate: false }
    );

    function initModules(): ModulesSettings {
      return setDefaultModules(props.config.item.modules);
    }

    onMounted(async () => {
      // Use workspaceId if it's passed from the payload (when clicked on EDIT SUBSCRIPTION in table),
      // otherwise use the workspaceId from the workspace store (currently selected hierarchy) as fallback.
      const hierarchyToUse = props.config.item.workspaceId ?? workspace.workspaceId;
      subscriptionRules.value = await useMspStore().getWorkspaceSubscriptionRules(hierarchyToUse);
    });

    return {
      form,
      localValue,
      selectedTab,
      valid,
      getEnabledModules,
      SubscriptionBundle,
      SubscriptionAddon,
      validate,
      subscriptionRules,
    };
  },
});
</script>

<style scoped lang="scss">
.dialog-content {
  min-width: 650px;
  max-width: 1150px;
  width: 100%;
  margin-right: 30px;
  margin-left: 30px;
}

.scroll-area {
  height: calc(100vh - 160px);
  overflow: auto;
}
</style>
